import { useFormContext } from 'react-hook-form';
import { CampaignSocialMediaType, useMarketplaceHashtagCloudLazyQuery } from '@anytag/graphql';
import { MAX_TIKTOK_HASHTAGS_LIMIT, UNEXPECTED_ERROR } from '@anytag/shared-constants';
import { useQueryHelper } from '@anytag/shared-hooks';
import { HashtagSuggestionField, HashtagSuggestionFieldProps } from '@anytag/shared-molecules';
import { MarketplaceFormKeys, MarketplaceFormValues } from '../types';
import { generateHashtagVariables } from '../utils';

export const HashtagField = <T extends string = keyof typeof MarketplaceFormKeys>({
  name,
  ...restProps
}: Partial<HashtagSuggestionFieldProps<T>>) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { getValues } = useFormContext<MarketplaceFormValues>();
  const { getMarketplaceHashtagCloud, data } = useMarketplaceHashtagCloudLazyQuery();

  const getMarketplaceHashtagsCloud = async (formValues: Partial<MarketplaceFormValues>) => {
    try {
      const variables = generateHashtagVariables(formValues);
      await getMarketplaceHashtagCloud({ variables });
    } catch (error) {
      enqueueSnackbar(t(error?.message || UNEXPECTED_ERROR), { variant: 'error' });
    }
  };

  const isTikTok = getValues('socialMedia') === CampaignSocialMediaType.TIKTOK;
  const maxHashtags = isTikTok ? MAX_TIKTOK_HASHTAGS_LIMIT : 10_000;

  return (
    <HashtagSuggestionField<T>
      maxHashtags={maxHashtags}
      notice={isTikTok ? t('TikTok now enforces a strict limit of a maximum of five (5) hashtags per post') : undefined}
      name={name || (MarketplaceFormKeys.hashtags as T)}
      hashtagsCloud={data?.marketplaceHashtagCloud || []}
      getHashtagsCloud={() => getMarketplaceHashtagsCloud(getValues())}
      {...restProps}
    />
  );
};
